import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  JoinedOccupation,
  buildOccupationSelection,
} from "../hooks/useOccupationSelection";
import type { OccupationTitlesDto } from "../api/dto/OccupationTitles";
import type { OccupationTypesDto } from "../api/dto/OccupationTypes";
import { useSlimFinancial } from "../context/SlimFinancialProvider";
import { unwrapApiP } from "../api/helpers/unwrapApi";

export type OccupationSelectionDto = {
  occupationTitles: OccupationTitlesDto["occupationTitles"];
  occupationTypes: OccupationTypesDto["occupationTypes"];
  joined: JoinedOccupation[];
  getTypeLabelById: (typeId?: number | string | null) => string;
  findByTitleId: (
    titleId?: number | string | null,
  ) => JoinedOccupation | undefined;
};

const selectionKeys = {
  root: ["occupationSelection"] as const,
  current: (partyId: string | number | undefined) =>
    [...selectionKeys.root, partyId] as const,
};

export const useOccupationSelectionQuery = (
  partyId?: string | number,
): UseQueryResult<OccupationSelectionDto | null, Error> => {
  const { getOccupationTitles, getOccupationTypes } = useSlimFinancial();

  return useQuery({
    queryKey: selectionKeys.current(partyId ?? "anon"),
    enabled: true, // or !!partyId if it must exist
    // fetch both resources in parallel and unwrap Either<Result> to raw DTOs
    queryFn: async () => {
      const [titlesDto, typesDto] = await Promise.all([
        unwrapApiP(getOccupationTitles.getTitles()), // -> OccupationTitlesDto
        unwrapApiP(getOccupationTypes.getTypes()), // -> OccupationTypesDto
      ]);
      return { titlesDto, typesDto };
    },
    // map to your view model
    select: ({ titlesDto, typesDto }) => {
      if (!titlesDto || !typesDto) return null;

      const titles = titlesDto.occupationTitles ?? [];
      const types = typesDto.occupationTypes ?? [];

      const selection = buildOccupationSelection(types, titles);

      const view: OccupationSelectionDto = {
        occupationTitles: titles,
        occupationTypes: types,
        joined: selection.joined,
        getTypeLabelById: selection.getTypeLabelById,
        findByTitleId: selection.findByTitleId,
      };

      return view;
    },
    staleTime: 5 * 60 * 1000, // cache for 5 minutes (tweak as needed)
  });
};
